<template>
  <v-container
    class="d-flex flex-column full-height"
    fluid
  >
    <w-row
      class="flex-grow-0"
      justify="space-between"
    >
      <slot
        name="prev-button"
        :prev-entry="handlePrevEntry"
      >
        <w-column
          cols="auto"
          class="d-flex align-center"
        >
          <WorkCalendarPrevBtn
            :disabled="calendarIsLoading"
            @click="handlePrevEntry"
          />
        </w-column>
      </slot>
      <slot
        v-if="$vuetify.breakpoint.xsOnly"
        name="next-button"
        :next-entry="handleNextEntry"
      >
        <w-column
          cols="auto"
          class="d-flex align-center"
        >
          <WorkCalendarNextBtn
            :disabled="calendarIsLoading"
            @click="handleNextEntry"
          />
        </w-column>
      </slot>
      <w-column
        cols="12"
        :sm="true"
      >
        <div v-show="type == 'day'">
          <WorkCalendarDateSelection
            v-model="syncedCalendarDate"
            :month="false"
          />
        </div>
        <div v-show="type != 'day'">
          <WorkCalendarDateSelection
            v-model="syncedCalendarDate"
            :month="true"
          />
        </div>
      </w-column>
      <w-column
        cols="12"
        :sm="true"
      >
        <WorkCalendarTypeSelection
          v-model="type"
          :items="calendarTypes"
        />
      </w-column>
      <w-column
        cols="12"
        :sm="true"
      >
        <WorkCalendarPeriodSelection
          v-model="period"
          :items="calendarPeriods"
        />
      </w-column>
      <slot
        v-if="$vuetify.breakpoint.smAndUp"
        name="next-button"
        :next-entry="handleNextEntry"
      >
        <w-column
          cols="auto"
          class="d-flex align-center"
        >
          <WorkCalendarNextBtn
            :disabled="calendarIsLoading"
            @click="handleNextEntry"
          />
        </w-column>
      </slot>
    </w-row>
    <WorkCalendarFilterBar
      v-if="$slots.filter"
      v-model="filters"
    >
      <slot name="filter" />
      <!-- Notice: Wrap filters in at least one w-row -->
    </WorkCalendarFilterBar>
    <w-row class="flex-grow-1">
      <w-column>
        <v-progress-linear
          v-if="calendarIsLoading"
          indeterminate
        />
        <v-calendar
          ref="calendar"
          v-model="syncedCalendarDate"
          class="w-calendar"
          :class="dynamicClasses"
          :weekdays="period"
          :type="type"
          :events="calendarEntries"
          :event-overlap-threshold="30"
          :event-color="_getColor"
          show-week
          @change="_getCalendarEntriesPipe"
          @click:event="handleShowCalendarEntry"
          @click:more="handleClickDate"
          @click:date="handleClickDate"
        />
        <WorkCalendarCard ref="calendarCard">
          <template #button="slotProps">
            <slot
              name="cardButton"
              :item="slotProps.item"
            />
          </template>
        </WorkCalendarCard>
      </w-column>
    </w-row>
  </v-container>
</template>

<script>
// data
import WORK_CALENDAR_TYPES from '@/components/global_components/calendar/data/WORK_CALENDAR_TYPES';
import WORK_CALENDAR_PERIODS from '@/components/global_components/calendar/data/WORK_CALENDAR_PERIODS';
// services & helpers
import dateService from '@/services/date-service';
import calendarApiService from '@/components/global_components/calendar/services/calendar-api-service';
import calendarDataHandlerService from '@/components/global_components/calendar/services/calendar-data-handler-service';
import REGEX from '@/validation/enum/REGEX';

export default {
  name: 'WorkCalendar',
  components: {
    WorkCalendarFilterBar: () =>
      import(
        '@/components/global_components/calendar/components/WorkCalendarFilterBar'
      ),
    WorkCalendarPeriodSelection: () =>
      import(
        '@/components/global_components/calendar/components/WorkCalendarPeriodSelection'
      ),
    WorkCalendarTypeSelection: () =>
      import(
        '@/components/global_components/calendar/components/WorkCalendarTypeSelection'
      ),
    WorkCalendarDateSelection: () =>
      import(
        '@/components/global_components/calendar/components/WorkCalendarDateSelection'
      ),
    WorkCalendarPrevBtn: () =>
      import(
        '@/components/global_components/calendar/components/WorkCalendarPrevBtn'
      ),
    WorkCalendarNextBtn: () =>
      import(
        '@/components/global_components/calendar/components/WorkCalendarNextBtn'
      ),
    WorkCalendarCard: () =>
      import(
        '@/components/global_components/calendar/components/WorkCalendarCard'
      ),
  },
  props: {
    path: String,
    calendarTypes: {
      type: Array,
      default: () => Object.values(WORK_CALENDAR_TYPES),
    },
    calendarPeriods: {
      type: Array,
      default: () => Object.values(WORK_CALENDAR_PERIODS),
    },
    defaultType: { type: String, default: WORK_CALENDAR_TYPES.MONTH.value },
    defaultPeriod: {
      type: Array,
      default: () => WORK_CALENDAR_PERIODS.FULL_WEEK.value,
    },
    defaultMonth: {
      type: String,
      default: dateService.getFirstDayOfCurrentMonth(),
      validator: function (value) {
        // use undefined instead of empty string
        return value !== '';
      },
    },
    getCalendarEntriesFromBackend: {
      type: Function,
      default: calendarApiService.getData,
    },
    mapCalendarBackendObjects: {
      type: Function,
      default:
        calendarDataHandlerService.mapCalendarApiResponseToCalendarObjects,
    },
    getCalendarEntries: {
      type: Function,
      default: calendarDataHandlerService.getCalendarEntries,
    },
    getColor: { type: Function, required: false },
    filterCalendarEntries: {
      type: Function,
      default: calendarDataHandlerService.filterCalendarEntries,
    },
  },
  data() {
    return {
      syncedCalendarDate: this.defaultMonth,
      type: this.defaultType,
      period: this.defaultPeriod,
      calendarIsLoading: false,
      calendarEntries: [],
      currentStartObject: undefined,
      filters: {},
    };
  },
  computed: {
    dynamicClasses() {
      return [this.calendarIsLoading ? 'w-calendar--disabled' : ''];
    },
  },
  watch: {
    filters: {
      handler() {
        this._getCalendarEntriesPipe({ start: this.currentStartObject });
      },
      deep: true,
    },
  },
  methods: {
    /* ### CALENDAR EVENT HANDLERS ### */
    handlePrevEntry() {
      this.$refs.calendar.prev();
    },
    handleNextEntry() {
      this.$refs.calendar.next();
    },
    handleShowCalendarEntry({ nativeEvent, event }) {
      if (this.calendarIsLoading) return;
      this.$refs.calendarCard.openCalendarEntry(nativeEvent, event);
    },
    handleClickDate({ date }) {
      if (this.calendarIsLoading) return;
      this.syncedCalendarDate = date;

      let newType = WORK_CALENDAR_TYPES.MONTH.value;
      if (this.type === WORK_CALENDAR_TYPES.MONTH.value) {
        newType = WORK_CALENDAR_TYPES.WEEK.value;
      }

      if (this.type === WORK_CALENDAR_TYPES.WEEK.value) {
        newType = WORK_CALENDAR_TYPES.DAY.value;
      }

      this.type = newType;
    },
    /* ### STATE MANAGEMENT ### */
    setCalendarToLoading() {
      this.calendarIsLoading = true;
    },
    setCalendarToNotLoading() {
      this.calendarIsLoading = false;
    },
    /* ### CALENDAR DATA HANDLE ### */
    async _getCalendarEntriesPipe({ start }) {
      this.setCalendarToLoading();
      this.currentStartObject = start;

      const currentRangeKey = calendarDataHandlerService.getRangeKey(start);
      this.calendarEntries = await this._loadCalendarEntries(
        start,
        currentRangeKey
      );

      this.setCalendarToNotLoading();
    },
    _getColor(event) {
      if (this.getColor != undefined) return this.getColor(event);
      return event.color;
    },
    async _loadCalendarEntries(start) {
      let currentCalendarEntries = await this.getCalendarEntries(
        start,
        this.getCalendarEntriesFromBackend(this.path),
        this.filters
      );

      currentCalendarEntries.data = currentCalendarEntries.data.data.map(e => e.data);

      currentCalendarEntries.data = this.convertDates(
        currentCalendarEntries.data
      );
      currentCalendarEntries = this.mapCalendarBackendObjects(
        currentCalendarEntries.data
      );
      return currentCalendarEntries;
    },
    convertDates(dates) {
      if (dates == null || dates.length <= 0) return dates;
      dates.forEach((date) => {
        date.start = this.convertToValidDate(date.startDate);
        date.start = this.addTime(date.start, date.startTime);
        date.end = this.convertToValidDate(date.endDate);
        date.end = this.addTime(date.end, date.endTime);
      });
      return dates;
    },
    convertToValidDate(date) {
      if (REGEX.DATE.test(date) == false) return date;

      let dateAsArray = date.split('.');
      return dateAsArray[2] + '-' + dateAsArray[1] + '-' + dateAsArray[0];
    },
    addTime(date, time) {
      if (time == null || REGEX.HOURS_MINUTE_REGEX.test(time) == false) return date;
      return date + ' ' + time;
    },
  },
};
</script>

<style lang="scss" scoped>
.w-calendar--disabled {
  opacity: $disabled-opacity;
}

::v-deep .w-calendar .v-outside .v-calendar-weekly__day-label {
  display: none; // hides days outside selected month (only in month mode)
}
</style>
